import React, { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from '@components/inputs/DateInput';
import DropDownMenu, { IChoice } from '@components/inputs/DropDownMenu';
import Label from '@components/inputs/text/Label';
import { Country } from '@utils/constants/localisation';
import { dateToISOFormat } from '@utils/misc/functions';
import { NavBar } from "@components/navigation/NavBar/NavBar";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { OnboardingDriverLicenceState, OnboardingDriverLicenceAction } from './onboardingDriverLicenceReducer';

interface FlexiOwnDriverLicenceV2Props {
  navigateTarget: string;
  onboardingDriverLicenceState: OnboardingDriverLicenceState;
  onboardingDriverLicenceDispatch: React.Dispatch<OnboardingDriverLicenceAction>;
  country: Country;
}

export default function FlexiOwnDriverLicenceV2({
  navigateTarget,
  onboardingDriverLicenceState,
  onboardingDriverLicenceDispatch,
  country
}: FlexiOwnDriverLicenceV2Props) {
  const navigate = useNavigate();
  const currentDate: Date = new Date();
  const dropDownPleaseSelect: IChoice = { label: '- please select -', value: '', enabled: false };

  const provinceByCountry: { [key in Country]: string[] } = {
    'GB': ['LDN'],
    'AU': ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'VIC', 'TAS', 'WA']
  };

  // Minimum age can be 23 years old. Applicable to AU & UK.
  const minimumAge: string = dateToISOFormat(
    new Date(currentDate.getFullYear() - 23, currentDate.getMonth(), currentDate.getDate())
  );
  // The maximum age a driver can be is 100 years old. This applies to both AU & UK
  const maximumAge: string = dateToISOFormat(
    new Date(currentDate.getFullYear() - 100, currentDate.getMonth(), currentDate.getDate())
  );
  // The maximum length of a driver's licence before it expires is 10 years, 11 is
  // used for safety. Applies to both AU & UK.
  const maxDriverLicence: string = dateToISOFormat(
    new Date(currentDate.getFullYear() + 11, currentDate.getMonth(), currentDate.getDate())
  );

  const onDriverLicenceChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onboardingDriverLicenceDispatch({ type: "SET_DRIVER_LICENCE_NUMBER", payload: e.target.value });

  const onStateChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onboardingDriverLicenceDispatch({ type: "SET_LICENCE_STATE", payload: e.target.value });

  const onExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onboardingDriverLicenceDispatch({ type: "SET_LICENCE_EXPIRY", payload: e.target.value });

  const onDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onboardingDriverLicenceDispatch({ type: "SET_DOB", payload: e.target.value });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };

  const textBoxStyle: React.CSSProperties = {
    height: '5vh',
    borderRadius: '5px',
    border: '1px solid #C4C4C4',
    fontSize: '18px',
    justifyContent: 'left',
    margin: '1vh 0vw',
    boxSizing: 'border-box',
    width: '50%',
    left: '28px',
  };

  return (
    <>
      <NavBar pageTitle={"Driver Licence"} backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={onSubmit}>
        <div style={{ margin: '2.5vh 0vw' }}>
          <p>
            Please review the customer's <strong>driver licence</strong> details, ensuring that each field is populated with the correct data.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', margin: '2.5vh 0vw' }}>
          <Label text={'Driver licence number'} requiredFieldInd={true} />
          <input
            style={textBoxStyle}
            name='driverLicenceNumber'
            defaultValue={onboardingDriverLicenceState.driverLicenceNumber}
            required={true}
            onChange={onDriverLicenceChange}
          />
        </div>

        {country == Country.AU && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', margin: '2.5vh 0vw' }}>
              <Label text={'State'} requiredFieldInd={true} />
              <DropDownMenu
                menuName={'state'}
                defaultVal={
                  onboardingDriverLicenceState.licenceState
                    ? { label: onboardingDriverLicenceState.licenceState, value: onboardingDriverLicenceState.licenceState, enabled: true }
                    : dropDownPleaseSelect
                }
                required={true}
                choices={[dropDownPleaseSelect].concat(
                  provinceByCountry[country].map((item: any) => ({ label: item, value: item, enabled: true }))
                )}
                onSelect={onStateChange}
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', margin: '2.5vh 0vw' }}>
          <Label text={'Expiry date'} requiredFieldInd={true} />
          <DatePicker
            name='driverLicenceExpiry'
            defaultVal={onboardingDriverLicenceState.licenceExpiry}
            min={dateToISOFormat(currentDate)}
            max={maxDriverLicence}
            required={true}
            onChange={onExpiryDateChange}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', margin: '2.5vh 0vw' }}>
          <Label text={'Date of birth'} requiredFieldInd={true} />
          <DatePicker
            name='dob'
            defaultVal={onboardingDriverLicenceState.dob}
            min={maximumAge}
            max={minimumAge}
            required={true}
            onChange={onDateOfBirthChange}
          />
        </div>

        <SubmitBtn text={"Next"} />
      </form>
    </>
  );
}